import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Box, Menu, MenuItem as MuiMenuItem } from "@mui/material";
import { useTranslation } from 'react-i18next';
import axios from "../utils/axiosConfig";
import { fetchFiles, getFile } from "../redux/actions/fileActions";
import BreadcrumbSection from "../components/BreadcrumbSection";
import TabsBar from "../components/TabsBar";
import CardsGrid from "../components/CardsGrid";
import OfficeMembersAndParties from "../components/OfficeMembersAndParties";
import DocumentsTable from "../components/DocumentsTable";
import TasksTable from "../components/TaskTable";
import TimesheetTable from "../components/TimeSheetTable";
import FinanceGraph from "../components/FinanceGraph";
import CalendarTable from "../components/CalendarTable";
import NotesTable from "../components/NotesTable";
import NotesDialog from "../components/NotesDialog";
import SalesPaymentPopup from "../components/SalesPaymentPopup";
import DisbursementTable from "../components/DisbursementTable";
import EmailsTable from "../components/EmailsTable";
import { formatDate } from "../utils/dateUtils";

const CasesPage = () => {
  const { t } = useTranslation(['pages', 'messages']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const [currentTab, setCurrentTab] = useState(tab || "overview");
  const [anchorEl, setAnchorEl] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const [selectedFile, setSelectedFile] = useState(null);
  const { file } = useSelector((state) => state.fileDetails);
  const [newNote, setNewNote] = useState("");
  const [newNoteDate, setNewNoteDate] = useState("");
  const [showNewNoteField, setShowNewNoteField] = useState(false);
  const [notes, setNotes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [popupData, setPopupData] = useState({
    caseId: "",
    from: null,
    to: null,
  });
  const [open, setOpen] = useState(false);
  const [financeActiveTab, setFinanceActiveTab] = useState(0);
  const [showDrafts, setShowDrafts] = useState(false);

  useEffect(() => {
    if (tab) {
      setCurrentTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    dispatch(getFile(id));
    dispatch(fetchFiles());
  }, [dispatch, id]);

  useEffect(() => {
    if (file?._id) {
      fetchNotes();
    }
  }, [file]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`/api/files/${file._id}`);
      console.log(response.data.notes);
      setNotes(response.data.notes);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateAppointment = () => {
    navigate("/appointment/create", { state: { caseId: id } });
    handleMenuClose();
  };

  const handleCreateHearing = () => {
    navigate("/hearing/create", { state: { caseId: id } });
    handleMenuClose();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenNotes = () => {
    setSelectedFile(file);
  };

  const handleCloseNotes = () => {
    setSelectedFile(null);
    setNewNote("");
    setNewNoteDate("");
    setShowNewNoteField(false);
  };

  const handleAddNote = () => {
    setNewNoteDate(formatDate(new Date()));
    setShowNewNoteField(true);
  };

  const handleSaveAllNotes = async () => {
    try {
      const updatePromises = notes.map((note) =>
        axios.put(`/api/files/${file._id}/notes/${note._id}`, {
          content: note.content,
        })
      );

      if (newNote) {
        updatePromises.push(
          axios.post(`/api/files/${file._id}/notes`, {
            content: newNote,
            date: new Date(),
          })
        );
      }

      await Promise.all(updatePromises);
      await fetchNotes();
      setNewNote("");
      setNewNoteDate("");
      setShowNewNoteField(false);
      setSnackbarMessage(t('messages:success.saved'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`/api/files/${file._id}/notes/${noteId}`);
      await fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      throw error; // Re-throw to let NotesTable handle the error
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleButtonClick = () => {
    switch (currentTab) {
      case "timesheets":
        const currentFile2 = files.find((file) => file._id === id);
        const owner2 = currentFile2
          ? currentFile2.officeMembers[0].member
          : null;
        navigate("/timesheets/create", { state: { caseId: id, owner2 } });
        break;
      case "tasks":
        const currentFile = files.find((file) => file._id === id);
        const owner = currentFile ? currentFile.officeMembers[0].member : null;
        navigate("/task/create", { state: { caseId: id, owner } });
        break;
      case "documents":
        navigate("/documents/upload", { state: { caseId: id } });
        break;
      case "disbursements":
        navigate("/disbursements/create", { state: { caseId: id } });
        break;
      default:
        console.log("Default button action");
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRegisterClientPayment = () => {
    const currentFile = files.find((file) => file._id === id);
    const from = currentFile ? currentFile.officeMembers[0].member : null;
    const to = currentFile ? currentFile.parties[0].name : null;
    setPopupData({ caseId: id, from, to });
    setOpen(true);
  };

  const handleCreateInvoice = () => {
    handleMenuItemClick("/finance/create-invoice", { state: { caseId: id } });
  };

  const handleCreateCredit = () => {
    handleMenuItemClick("/finance/create-credit", { state: { caseId: id } });
  };

  const handleCreateProvisionNote = () => {
    handleMenuItemClick("/finance/create-provision", { state: { caseId: id } });
  };

  const handleMenuItemClick = (path) => {
    const currentFile = files.find((file) => file._id === id);
    const from = currentFile ? currentFile.officeMembers[0].member : null;
    const to = currentFile ? currentFile.parties[0].name : null;
    navigate(path, { state: { caseId: id, from, to } });
    handleMenuClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFinanceTabChange = (newActiveTab) => {
    setFinanceActiveTab(newActiveTab);
  };

  return (
    <Container maxWidth="xl" sx={{ padding: 0 }}>
      <Box
        sx={{
          padding: "12px 24px 0 24px",
          backgroundColor: "white",
          // borderBottom: "1px solid black",
          margin: "0px"
        }}
      >
        <BreadcrumbSection
          file={file}
          currentTab={currentTab}
          financeActiveTab={financeActiveTab}
          handleButtonClick={handleButtonClick}
          handleMenuClick={handleMenuClick}
          anchorEl={anchorEl}
          handleMenuClose={handleMenuClose}
          handleCreateAppointment={handleCreateAppointment}
          handleCreateHearing={handleCreateHearing}
          handleOpenNotes={handleOpenNotes}
          handleMenuOpen={handleMenuOpen}
          handleRegisterClientPayment={handleRegisterClientPayment}
          handleCreateInvoice={handleCreateInvoice}
          handleCreateCredit={handleCreateCredit}
          handleCreateProvisionNote={handleCreateProvisionNote}
          showDrafts={showDrafts}
          setShowDrafts={setShowDrafts}
        />
        <Box sx={{ position: 'relative', bottom: "-1px", zIndex: 10 }}>
          <TabsBar setCurrentTab={setCurrentTab} />
        </Box>
      </Box>

      <Box>
        <div
          style={{
            backgroundColor: "#ECECEC",
            marginTop: "0",
            paddingBottom: "40px",
          }}
        >
          <div>
            {currentTab === "overview" && <CardsGrid />}
            {currentTab === "parties" && <OfficeMembersAndParties />}
            {currentTab === "tasks" && <TasksTable />}
            {currentTab === "timesheets" && <TimesheetTable />}
            {currentTab === "finance" && (
              <FinanceGraph onTabChange={handleFinanceTabChange} />
            )}
            {currentTab === "documents" && <DocumentsTable />}
            {currentTab === "calendar" && <CalendarTable />}
            {currentTab === "disbursements" && <DisbursementTable />}
            {currentTab === "emails" && <EmailsTable showDrafts={showDrafts} />}
            {currentTab === "notes" && (
              <Box>
                <NotesTable
                  notes={notes}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleOpenNotes={handleOpenNotes}
                  file={file}
                  onDeleteNote={handleDeleteNote}
                />
                <NotesDialog
                  selectedFile={selectedFile}
                  file={file}
                  notes={notes}
                  newNote={newNote}
                  newNoteDate={newNoteDate}
                  showNewNoteField={showNewNoteField}
                  snackbarOpen={snackbarOpen}
                  snackbarMessage={snackbarMessage}
                  handleCloseNotes={handleCloseNotes}
                  handleAddNote={handleAddNote}
                  handleSaveAllNotes={handleSaveAllNotes}
                  handleDeleteNote={handleDeleteNote}
                  setNewNote={setNewNote}
                  setShowNewNoteField={setShowNewNoteField}
                  setNotes={setNotes}
                  setSnackbarOpen={setSnackbarOpen}
                />
              </Box>
            )}
          </div>
        </div>
      </Box>
      <SalesPaymentPopup
        open={open}
        onClose={handleClose}
        popupData={popupData}
      />
    </Container>
  );
};

export default CasesPage;
